import { ApiClient } from '@/lib/api-client';
import { 
  FavoriteWithRecipe,
  FavoritesResponse,
  ToggleFavoriteRequest,
  ToggleFavoriteResponse,
  FavoriteStatusesRequest,
  FavoriteStatusesResponse
} from '@/types/api';

export class FavoritesService {
  /**
   * Toggle favorite status for a recipe
   */
  static async toggleFavorite(data: ToggleFavoriteRequest): Promise<ToggleFavoriteResponse> {
    const response = await ApiClient.post<ToggleFavoriteResponse>('/favorites/toggle', data);
    
    if (response.success && response.data) {
      return response.data;
    }
    
    throw new Error(response.message || 'Failed to toggle favorite');
  }

  /**
   * Add recipe to favorites
   */
  static async addFavorite(recipeId: string): Promise<FavoriteWithRecipe> {
    const response = await ApiClient.post<FavoriteWithRecipe>('/favorites', { recipeId });
    
    if (response.success && response.data) {
      return response.data;
    }
    
    throw new Error(response.message || 'Failed to add favorite');
  }

  /**
   * Remove recipe from favorites
   */
  static async removeFavorite(recipeId: string): Promise<void> {
    const response = await ApiClient.delete(`/favorites/${recipeId}`);
    
    if (!response.success) {
      throw new Error(response.message || 'Failed to remove favorite');
    }
  }

  /**
   * Get user's favorite recipes with pagination
   */
  static async getFavorites(page: number = 1, limit: number = 20): Promise<FavoritesResponse> {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString()
    });

    const response = await ApiClient.get<FavoritesResponse>(`/favorites?${params.toString()}`);
    
    if (response.success && response.data) {
      return response.data;
    }
    
    throw new Error(response.message || 'Failed to get favorites');
  }

  /**
   * Check if a recipe is favorited
   */
  static async isFavorite(recipeId: string): Promise<boolean> {
    const response = await ApiClient.get<{ isFavorite: boolean }>(`/favorites/${recipeId}`);
    
    if (response.success && response.data) {
      return response.data.isFavorite;
    }
    
    throw new Error(response.message || 'Failed to check favorite status');
  }

  /**
   * Get favorite status for multiple recipes
   */
  static async getFavoriteStatuses(data: FavoriteStatusesRequest): Promise<Record<string, boolean>> {
    const response = await ApiClient.post<FavoriteStatusesResponse>('/favorites/statuses', data);
    
    if (response.success && response.data) {
      return response.data.statuses;
    }
    
    throw new Error(response.message || 'Failed to get favorite statuses');
  }

  /**
   * Get total count of user's favorites
   */
  static async getFavoriteCount(): Promise<number> {
    const response = await ApiClient.get<{ count: number }>('/favorites/count');
    
    if (response.success && response.data) {
      return response.data.count;
    }
    
    throw new Error(response.message || 'Failed to get favorite count');
  }

  /**
   * Clear all favorites
   */
  static async clearFavorites(): Promise<number> {
    const response = await ApiClient.delete<{ clearedCount: number }>('/favorites/clear');
    
    if (response.success && response.data) {
      return response.data.clearedCount;
    }
    
    throw new Error(response.message || 'Failed to clear favorites');
  }

  /**
   * Get all favorites (without pagination) - useful for synchronization
   */
  static async getAllFavorites(): Promise<FavoriteWithRecipe[]> {
    let allFavorites: FavoriteWithRecipe[] = [];
    let currentPage = 1;
    let hasMore = true;

    while (hasMore) {
      try {
        const response = await this.getFavorites(currentPage, 50); // Get 50 per page
        allFavorites = allFavorites.concat(response.favorites);
        
        hasMore = currentPage < response.totalPages;
        currentPage++;
      } catch (error) {
        console.error('Error fetching favorites page:', currentPage, error);
        break;
      }
    }

    return allFavorites;
  }

  /**
   * Sync favorites with server - useful for offline/online sync
   */
  static async syncFavorites(localFavoriteIds: string[]): Promise<{
    serverFavorites: FavoriteWithRecipe[];
    conflictResolution: 'server' | 'local';
  }> {
    try {
      // Get server favorites
      const serverFavorites = await this.getAllFavorites();
      const serverFavoriteIds = new Set(serverFavorites.map(f => f.recipeId));
      
      // Find differences
      const toAddToServer = localFavoriteIds.filter(id => !serverFavoriteIds.has(id));
      // TODO: Implement server-side removal for full bidirectional sync
      // const localSet = new Set(localFavoriteIds);
      // const toRemoveFromServer = serverFavorites
      //   .filter(f => !localSet.has(f.recipeId))
      //   .map(f => f.recipeId);

      // Sync differences (add missing to server)
      for (const recipeId of toAddToServer) {
        try {
          await this.addFavorite(recipeId);
        } catch (error) {
          console.warn('Failed to sync favorite to server:', recipeId, error);
        }
      }

      // Note: We don't automatically remove from server, as that could be destructive
      // Instead, we return the server state as the source of truth
      
      return {
        serverFavorites: await this.getAllFavorites(), // Get fresh data after sync
        conflictResolution: 'server' // Server wins in conflicts
      };
    } catch (error) {
      console.error('Failed to sync favorites:', error);
      throw error;
    }
  }
}
